import './InfiniteSearchSelect.scss';

import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useDebounce } from 'hooks';
import React, { useMemo, useState } from 'react';
import { useFetchInfiniteList } from 'services/common/react-query-common-service';

import StylishLoader from '../StylishLoader/StylishLoader';

interface InfiniteSearchSelectProps {
  label: string;
  placeholder: string;
  endpoint: string;
  queryKey: string;
  value: any;
  onChange: (value: any) => void;
  error?: boolean;
  helperText?: string;
  multiple?: boolean;
  mapResponse?: (data: any) => any[];
  initialOptions?: Array<{ label: string; value: string }>;
  sx?: any;
  extraParams?: string;
  onSelect?: (option: { label: string; value: string }) => void;
}

const InfiniteSearchSelect: React.FC<InfiniteSearchSelectProps> = ({
  placeholder,
  endpoint,
  queryKey,
  value,
  onChange,
  error,
  helperText,
  multiple = false,
  mapResponse = (data) =>
    data.data.map((item: any) => ({
      label: item.clientName || item.name,
      value: String(item.id),
    })),
  initialOptions = [],
  sx,
  extraParams = '',
  onSelect,
}) => {
  const [selectedOptions, setSelectedOptions] =
    useState<Array<{ label: string; value: string }>>(initialOptions);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useFetchInfiniteList(
      [queryKey, debouncedSearch],
      `${String(endpoint)}${
        String(endpoint).includes('?') ? '&' : '?'
      }search=${String(debouncedSearch)}${
        extraParams ? `&${String(extraParams)}` : ''
      }`,
      { enabled: open },
    );

  const options = useMemo(() => {
    const fetchedOptions = data?.pages.flatMap(mapResponse) || [];
    const combined = [...selectedOptions];

    fetchedOptions.forEach((opt) => {
      if (!combined.some((c) => c.value === opt.value)) {
        combined.push(opt);
      }
    });

    return combined;
  }, [data, mapResponse, selectedOptions]);

  // Update selectedOptions memory when options update or value updates
  React.useEffect(() => {
    const currentValues = Array.isArray(value) ? value : [value];
    const newSelected = [...selectedOptions];
    let changed = false;

    currentValues.forEach((val) => {
      const stringVal = String(val);
      if (!stringVal) return;

      // If we don't have this label in our memory, try to find it in current options
      if (!newSelected.some((opt) => opt.value === stringVal)) {
        const found = options.find((opt) => opt.value === stringVal);
        if (found) {
          newSelected.push(found);
          changed = true;
        }
      }
    });

    if (changed) {
      setSelectedOptions(newSelected);
    }
  }, [value, options, selectedOptions]);

  // Sync with initialOptions when they change (e.g. when opening modal for a different team)
  React.useEffect(() => {
    if (initialOptions && initialOptions.length > 0) {
      setSelectedOptions((prev) => {
        const next = [...prev];
        let changed = false;
        initialOptions.forEach((opt) => {
          if (!next.some((n) => n.value === opt.value)) {
            next.push(opt);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [initialOptions]);

  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const listboxNode = event.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = listboxNode;

    // Trigger when user is within 50px of the bottom
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (isNearBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <FormControl
      className="infinite-search-select-container"
      error={error}
      fullWidth
      sx={sx}>
      <Select
        displayEmpty
        multiple={multiple}
        onClose={() => {
          setOpen(false);
          setSearchTerm('');
        }}
        onOpen={() => setOpen(true)}
        open={open}
        renderValue={(selected) => {
          if (multiple) {
            if (
              !selected ||
              (Array.isArray(selected) && selected.length === 0)
            ) {
              return (
                <Box
                  component="span"
                  className="render-value placeholder"
                  sx={{
                    color: '#9ca3af',
                    fontFamily: 'Inter !important',
                    fontSize: '13px !important',
                  }}>
                  {placeholder}
                </Box>
              );
            }

            const selectedArray = Array.isArray(selected)
              ? selected
              : [selected];
            const labels = selectedArray.map((val) => {
              const opt = options.find((o) => o.value === String(val));
              return opt ? opt.label : val;
            });

            return (
              <Box
                component="span"
                className="render-value"
                sx={{
                  color: '#242424',
                  fontFamily: 'Inter !important',
                  fontSize: '13px !important',
                  fontWeight: 500,
                }}>
                {labels.join(', ')}
              </Box>
            );
          }
          const selectedOpt = options.find((opt) => opt.value === selected);
          return (
            <Box
              component="span"
              className={
                selectedOpt ? 'render-value' : 'render-value placeholder'
              }
              sx={{
                color: selectedOpt ? '#242424' : '#9ca3af',
                fontFamily: 'Inter !important',
                fontSize: '13px !important',
                fontWeight: selectedOpt ? 500 : 400,
              }}>
              {selectedOpt ? selectedOpt.label : placeholder}
            </Box>
          );
        }}
        value={value}
        MenuProps={{
          MenuListProps: {
            onScroll: handleScroll,
            style: { maxHeight: 300, overflow: 'auto' },
          } as any,
          PaperProps: {
            sx: {
              '& .MuiList-root': {
                maxHeight: 300,
                overflow: 'auto',
                paddingTop: 0,
              },
            },
          },
        }}
        onChange={(e) => {
          const val = e.target.value;
          onChange(val);
          if (onSelect) {
            const selectedOpt = options.find(
              (opt) => opt.value === String(val),
            );
            if (selectedOpt) onSelect(selectedOpt);
          }
        }}>
        <Box
          className="infinite-search-header"
          onKeyDown={(e) => e.stopPropagation()}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search..."
            value={searchTerm}
            autoFocus
            className="search-field"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>
        {options.map((option) => {
          const isSelected = multiple
            ? Array.isArray(value) && value.includes(option.value)
            : value === option.value;

          return (
            <MenuItem
              key={option.value}
              value={option.value}
              className="infinite-search-option">
              {multiple && (
                <Checkbox
                  checked={isSelected}
                  sx={{ marginRight: 1, padding: '4px' }}
                />
              )}
              {option.label}
            </MenuItem>
          );
        })}
        {(isLoading || isFetchingNextPage) && (
          <Box
            className="infinite-search-loading"
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
            }}>
            <StylishLoader fullPage={false} />
          </Box>
        )}
        {!isLoading && options.length === 0 && (
          <MenuItem disabled className="infinite-search-no-results">
            No results found
          </MenuItem>
        )}
      </Select>
      {helperText && (
        <FormHelperText className="infinite-search-helper-text">
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default InfiniteSearchSelect;
